import type { DocumentType, GFADocumentType, S3File } from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@dune/ui";
import {
	documentTypeLabels,
	documentTypes,
	gfaDocumentTypeLabels,
	gfaDocumentTypes,
} from "@dune/vars";
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { useUploadConstructionSiteDocumentMutation } from "../../../../api/mutations/constructionSites.mutations";
import { useLinkFileToGFA } from "../../../../api/mutations/gfa.mutations";
import { S3FileInput } from "../../../../component/common/S3FileInput";
import {
	getSignedPutUrl,
	registerS3File,
	uploadFileToS3,
} from "../../../../api/queries/files.queries";

interface UploadDocumentModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	constructionSiteId: number;
	isGFA?: boolean;
}

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
	open,
	onOpenChange,
	constructionSiteId,
	isGFA = false,
}) => {
	const [selectedFile, setSelectedFile] = useState<S3File | null>(null);
	const [selectedFileForUpload, setSelectedFileForUpload] =
		useState<File | null>(null);
	const [documentType, setDocumentType] = useState<string>("");
	const [isUploadingFile, setIsUploadingFile] = useState(false);

	const uploadDocumentMutation = useUploadConstructionSiteDocumentMutation();
	const linkFileToGFAMutation = useLinkFileToGFA(
		isGFA ? constructionSiteId : 0,
		{ invalidateDocuments: true },
	);

	const availableDocumentTypes = useMemo(() => {
		return isGFA ? gfaDocumentTypes : documentTypes;
	}, [isGFA]);

	const getDocumentTypeLabel = (type: string): string => {
		const labels = isGFA ? gfaDocumentTypeLabels : documentTypeLabels;
		return labels[type as keyof typeof labels] ?? type;
	};

	const handleUpload = async () => {
		if (isGFA) {
			if (!selectedFileForUpload || !documentType) {
				toast.error("Veuillez sélectionner un fichier et un type de document");
				return;
			}
		} else {
			if (!selectedFile || !documentType) {
				toast.error("Veuillez sélectionner un fichier et un type de document");
				return;
			}
		}

		try {
			if (isGFA) {
				if (!selectedFileForUpload) return;
				setIsUploadingFile(true);
				const signedUrl = await getSignedPutUrl({
					bucket: "private",
					contentType: selectedFileForUpload.type,
				});

				await uploadFileToS3(signedUrl.signedUrl, selectedFileForUpload);

				const registeredFile = await registerS3File({
					bucket: "private",
					key: signedUrl.key,
					name: selectedFileForUpload.name,
					mimeType: selectedFileForUpload.type,
					size: selectedFileForUpload.size,
				});

				await linkFileToGFAMutation.mutateAsync({
					fileId: registeredFile.id,
					type: documentType as GFADocumentType,
				});
			} else {
				if (!selectedFile) return;
				await uploadDocumentMutation.mutateAsync({
					constructionSiteId,
					name: selectedFile.name,
					key: selectedFile.key,
					bucket: selectedFile.bucket,
					mimeType: selectedFile.type,
					type: documentType as DocumentType,
				});
			}

			toast.success("Document uploadé avec succès");
			onOpenChange(false);
			setSelectedFile(null);
			setSelectedFileForUpload(null);
			setDocumentType("");
		} catch (error) {
			console.error("Upload failed:", error);
		} finally {
			setIsUploadingFile(false);
		}
	};

	const handleModalClose = (newOpen: boolean) => {
		if (!newOpen) {
			setSelectedFile(null);
			setSelectedFileForUpload(null);
			setDocumentType("");
		}
		onOpenChange(newOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleModalClose}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>Ajouter un document</DialogTitle>
					<DialogDescription>
						Sélectionnez un fichier et choisissez le type de document
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div>
						<div className="text-sm font-medium mb-2">Type de document</div>
						<Select
							value={documentType}
							onValueChange={(value) => setDocumentType(value)}
						>
							<SelectTrigger>
								<SelectValue placeholder="Sélectionnez un type de document" />
							</SelectTrigger>
							<SelectContent>
								{availableDocumentTypes.map((option) => (
									<SelectItem key={option} value={option}>
										{getDocumentTypeLabel(option)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div>
						<div className="text-sm font-medium mb-2">Fichier</div>
						{isGFA ? (
							<input
								type="file"
								onChange={(e) => {
									const file = e.target.files?.[0];
									if (file) {
										setSelectedFileForUpload(file);
									}
								}}
								className="w-full border rounded-md p-2"
								accept=".pdf,.docx,.png,.jpg,.jpeg"
							/>
						) : (
							<S3FileInput
								key={`upload-modal-${open}`}
								bucket="private"
								onUploadSuccess={setSelectedFile}
								hidePreviewButton={true}
								subText="Formats acceptés : PDF, DOCX, PNG, JPG (max. 10MB)"
							/>
						)}
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Annuler
					</Button>
					<Button
						onClick={handleUpload}
						disabled={
							(isGFA ? !selectedFileForUpload : !selectedFile) ||
							!documentType ||
							uploadDocumentMutation.isPending ||
							linkFileToGFAMutation.isPending ||
							isUploadingFile
						}
						isLoading={
							uploadDocumentMutation.isPending ||
							linkFileToGFAMutation.isPending ||
							isUploadingFile
						}
					>
						Ajouter le document
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default UploadDocumentModal;
